import { PublicCallback } from 'globals/types';
import * as AssistantAPI from 'core_main/api/assistant';
import * as AutomationsMetadataApi from 'core_main/api/automations_metadata';
import { maybeFetchCurrentTrackingToken } from 'core_main/api/tracking';
import { InitAssistantParameters } from 'core_main/api/assistant/types';
import { GetAutomationsMetadataApiResponse } from 'core_main/api/automations_metadata/types';
import { getStore } from 'core_main/store';
import {
  createInitAssistantCallbackResponse,
  normalizeAutomationMetadataAttributes,
  normalizeInitialMessages,
} from './helpers';
import { InitAssistantCallbackResponse } from './types';

export const initAssistant = (
  params: InitAssistantParameters,
  cb?: PublicCallback,
): Promise<InitAssistantCallbackResponse> => {
  const { aiAgentTeamId } = params;
  const isTestMode = !!getStore().testAutomationId; // we are in test mode if a testAutomationId was set via the Start SDK method

  return maybeFetchCurrentTrackingToken()
    .then(() => {
      if (aiAgentTeamId) {
        return AutomationsMetadataApi.fetchAutomationsMetadata();
      }

      return AssistantAPI.initAssistant(params);
    })
    .then((res: GetAutomationsMetadataApiResponse) => {
      let callbackData;

      if (aiAgentTeamId) {
        const automationMetadata = res?.data?.find((automationMetadata) => {
          // Only check testAutomationId when explicitly in test mode
          // Otherwise check liveAutomationId for production automations
          return isTestMode
            ? automationMetadata.attributes.testAutomationId === aiAgentTeamId
            : automationMetadata.attributes.liveAutomationId === aiAgentTeamId;
        });

        if (!automationMetadata) {
          console.error(
            '[initAssistant] No automation matched. Searched for:',
            {
              aiAgentTeamId,
              searchField: isTestMode ? 'testAutomationId' : 'liveAutomationId',
              isTestMode,
            },
          );
        }

        const attributes = automationMetadata?.attributes;
        const initialMessages = attributes?.proactive?.chat?.messages;

        // Fail only if we can't find the automation at all
        if (!attributes) {
          console.error(
            '[initAssistant] Automation not found for aiAgentTeamId:',
            aiAgentTeamId,
          );
          return Promise.reject(
            new Error('Automation not found for AI Agent Team'),
          );
        }

        callbackData = {
          attributes: normalizeAutomationMetadataAttributes(attributes),
          initialMessages: initialMessages?.length
            ? normalizeInitialMessages(initialMessages)
            : [],
        };
      } else {
        callbackData = res.data;
      }

      const callbackResponse =
        createInitAssistantCallbackResponse(callbackData);
      if (typeof cb === 'function') {
        cb(callbackResponse);
      }
      return callbackResponse;
    })
    .catch((error) => {
      if (typeof cb === 'function') cb(null, error);
      throw new Error(error);
    });
};
